import { useEffect, useState } from "react";
import type { TocItem } from "@/lib/article-toc";
import type { Locale } from "@/lib/i18n";

interface Props {
  items: TocItem[];
  locale: Locale;
}

function useActiveHeading(ids: string[]) {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  useEffect(() => {
    if (ids.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const firstVisible = ids.find((id) => visible.has(id));
        if (firstVisible) setActive(firstVisible);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: [0, 1] },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}

function TocList({
  items,
  active,
  onClick,
}: {
  items: TocItem[];
  active: string | null;
  onClick?: () => void;
}) {
  return (
    <ul className="space-y-1 text-sm">
      {items.map((it) => {
        const isActive = it.id === active;
        return (
          <li key={it.id} className={it.level === 3 ? "pl-3" : ""}>
            <a
              href={`#${it.id}`}
              onClick={onClick}
              aria-current={isActive ? "location" : undefined}
              className={`block rounded-sm border-l-2 py-1 pl-3 leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                isActive
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              {it.text}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

export function ArticleTOC({ items, locale }: Props) {
  const ids = items.map((i) => i.id);
  const active = useActiveHeading(ids);
  if (items.length < 3) return null;
  const label = locale === "es" ? "En esta página" : "On this page";

  return (
    <>
      <details className="group mt-8 rounded-2xl border border-border bg-card/40 p-4 lg:hidden">
        <summary className="cursor-pointer list-none text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {label}
          <span className="float-right transition-transform group-open:rotate-180">▾</span>
        </summary>
        <nav aria-label={label} className="mt-4">
          <TocList
            items={items}
            active={active}
            onClick={() => {
              const el = document.querySelector("details[open]") as HTMLDetailsElement | null;
              if (el) el.open = false;
            }}
          />
        </nav>
      </details>

      <aside className="hidden lg:block">
        <div className="sticky top-28">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {label}
          </p>
          <nav aria-label={label}>
            <TocList items={items} active={active} />
          </nav>
        </div>
      </aside>
    </>
  );
}